/**
 * Onboarding Utilities
 * Epic 13: Central de Ajuda e Suporte
 * Story 13.4: Onboarding Guiado - Helper Functions
 *
 * Provides utility functions for onboarding features:
 * - Manual deep linking (Task 17)
 * - URL generation
 * - Step content enhancement
 */

import { getSectionBySlug } from "@/services/manualApi";

// ============================================================================
// Manual Deep Linking (Task 17)
// ============================================================================

/**
 * Generate manual deep link URL for a section
 *
 * @param sectionSlug - The manual section slug
 * @param moduleKey - Optional module key for context
 * @returns URL to manual section
 */
export const getManualDeepLink = (sectionSlug: string, moduleKey?: string): string => {
  const params = new URLSearchParams();
  if (moduleKey) {
    params.append("module", moduleKey);
  }
  params.append("section", sectionSlug);

  return `/ajuda/manual?${params.toString()}`;
};

/**
 * Get section ID from section slug (helper for backend compatibility)
 *
 * Note: Backend tour definitions use manual_section_id (UUID), but we need slug for navigation.
 * This function would typically fetch the section to get its slug.
 * For now, we assume the backend can provide slugs directly or we maintain a mapping.
 *
 * @param sectionId - Manual section UUID
 * @returns Promise<string> - Section slug for URL generation
 */
export const getSectionSlugById = async (sectionId: string): Promise<string | null> => {
  try {
    // This would require a new backend endpoint: GET /manual/sections/by-id/{id}
    // For now, we'll return null and handle it gracefully
    // TODO: Add backend endpoint or update tour definitions to include slugs
    return null;
  } catch (error) {
    console.error("Failed to fetch section slug:", error);
    return null;
  }
};

/**
 * Enhance tour step text with manual link button
 *
 * @param stepText - Original step text
 * @param manualSectionSlug - Optional manual section slug
 * @returns Enhanced HTML with manual link
 */
export const enhanceStepTextWithManualLink = (
  stepText: string,
  manualSectionSlug?: string
): string => {
  if (!manualSectionSlug) {
    return stepText;
  }

  const manualLink = getManualDeepLink(manualSectionSlug);

  return `
    ${stepText}
    <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(0,0,0,0.1);">
      <a
        href="${manualLink}"
        target="_blank"
        style="
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: #2563eb;
          text-decoration: none;
          font-weight: 500;
        "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
        Ver no Manual
      </a>
    </div>
  `;
};

// ============================================================================
// Tour Step Navigation
// ============================================================================

/**
 * Check if user should see tour based on profile
 *
 * @param targetRoles - Tour target roles
 * @param userRole - Current user role
 * @returns boolean - Whether user can access this tour
 */
export const canAccessTour = (targetRoles: string[], userRole: string): boolean => {
  return targetRoles.some((role) => role.toLowerCase() === userRole.toLowerCase());
};

/**
 * Calculate estimated tour duration
 *
 * @param stepCount - Number of steps in tour
 * @param averageTimePerStep - Average time per step in seconds (default: 30s)
 * @returns Duration in minutes
 */
export const estimateTourDuration = (stepCount: number, averageTimePerStep = 30): number => {
  return Math.ceil((stepCount * averageTimePerStep) / 60);
};

// ============================================================================
// Shepherd.js Helpers
// ============================================================================

/**
 * Default Shepherd.js theme classes
 */
export const SHEPHERD_THEME_CLASSES = {
  wrapper: "shepherd-theme-custom",
  arrow: "shepherd-arrow",
  button: {
    primary: "shepherd-button-primary",
    secondary: "shepherd-button-secondary",
  },
} as const;

/**
 * Get scroll offset for tour steps
 * Accounts for fixed header height
 *
 * @returns Scroll offset object for Shepherd.js
 */
export const getScrollOffset = () => {
  return { top: 80 }; // Fixed header height
};

export default {
  getManualDeepLink,
  getSectionSlugById,
  enhanceStepTextWithManualLink,
  canAccessTour,
  estimateTourDuration,
  SHEPHERD_THEME_CLASSES,
  getScrollOffset,
};
